/**
 * The one place a typed amount becomes integer minor units (CLAUDE.md rule 1). Pure string work: no
 * floating point is ever involved, so `"1499.5"` is exactly 149950 and `"0.29"` is exactly 29.
 * The number of minor digits comes from the currency (Intl), never from an India default (rule 2).
 */

export type MoneyInputError = "empty" | "invalid" | "negative" | "too_many_decimals" | "too_large";
export type MoneyInputResult = { ok: true; minor: number } | { ok: false; reason: MoneyInputError };

/** Minor digits of a currency: 2 for INR/USD, 0 for JPY, 3 for KWD. */
export function minorDigits(currency: string): number {
  return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
}

/** How this currency is written in front of an amount, longest first: its code and its symbols. */
function currencyPrefixes(currency: string): string[] {
  const symbol = (currencyDisplay: "symbol" | "narrowSymbol") =>
    new Intl.NumberFormat("en", { style: "currency", currency, currencyDisplay }).formatToParts(0).find((p) => p.type === "currency")?.value ?? "";
  return [currency, symbol("symbol"), symbol("narrowSymbol")].filter(Boolean).sort((a, b) => b.length - a.length);
}

/**
 * `parseMoneyInput("1,499", "INR")` → `{ ok: true, minor: 149900 }`.
 * Accepts a leading symbol or code of this currency, spaces and `,` thousands separators; `.` is the decimal mark.
 * Refuses: empty, negative, letters (a guessed prefix could turn `Rs.50` into 50 paise, or read `$5` as rupees),
 * more decimals than the currency has, and anything past the safe-integer range.
 */
export function parseMoneyInput(raw: string, currency: string): MoneyInputResult {
  const digits = minorDigits(currency);
  const compact = raw.replace(/[\s,]/g, "");
  const prefix = currencyPrefixes(currency).find((p) => compact.toUpperCase().startsWith(p.toUpperCase()));
  const cleaned = prefix ? compact.slice(prefix.length) : compact;
  if (cleaned === "") return { ok: false, reason: "empty" };
  if (cleaned.startsWith("-")) return /^-0*\.?0*$/.test(cleaned) ? { ok: true, minor: 0 } : { ok: false, reason: "negative" };
  const m = /^\+?(\d*)(?:\.(\d*))?$/.exec(cleaned);
  if (!m || (m[1] === "" && (m[2] ?? "") === "")) return { ok: false, reason: "invalid" };
  const whole = m[1] || "0";
  const fraction = m[2] ?? "";
  if (fraction.length > digits) return { ok: false, reason: "too_many_decimals" };
  const minor = Number(`${whole}${fraction.padEnd(digits, "0")}`);
  if (!Number.isSafeInteger(minor)) return { ok: false, reason: "too_large" };
  return { ok: true, minor };
}

/** Back into the text a field shows: `149950` → `"1499.50"`, `149900` → `"1499"`. Display only. */
export function minorToInput(minor: number, currency: string): string {
  return decimalText(minor, minorDigits(currency));
}

function decimalText(minor: number, digits: number): string {
  if (!Number.isSafeInteger(minor) || minor < 0) throw new RangeError("needs a non-negative integer");
  if (digits === 0) return String(minor);
  const s = String(minor).padStart(digits + 1, "0");
  const whole = s.slice(0, -digits);
  const fraction = s.slice(-digits).replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole;
}

/** Percent typed by a person to basis points on the wire: `"18"` → 1800, `"2.5"` → 250. Two decimals at most. */
export function parsePercentInput(raw: string): { ok: true; bp: number } | { ok: false; reason: MoneyInputError } {
  const cleaned = raw.replace(/[\s%]/g, "");
  if (cleaned === "") return { ok: false, reason: "empty" };
  if (cleaned.startsWith("-")) return { ok: false, reason: "negative" };
  const m = /^(\d*)(?:\.(\d*))?$/.exec(cleaned);
  if (!m || (m[1] === "" && (m[2] ?? "") === "")) return { ok: false, reason: "invalid" };
  const fraction = m[2] ?? "";
  if (fraction.length > 2) return { ok: false, reason: "too_many_decimals" };
  return { ok: true, bp: Number(`${m[1] || "0"}${fraction.padEnd(2, "0")}`) };
}

/** Basis points back into the text a field shows: `1800` → `"18"`, `250` → `"2.5"`. */
export const bpToPercent = (bp: number): string => decimalText(bp, 2);
